/* =============================================================================
   CORE / RESERVATION-ENGINE — Regel-Engine + Tisch-Zuweisungs-Algorithmus
   =============================================================================
   Drittes echtes ES-Modul der Codebase (Phase 1.3). Bündelt die komplette
   Reservierungs-Entscheidungs-Logik:
     - Tisch-Geometrie (Größe, Abstand) und Adjazenz-Cluster-Check
     - Natural-Sort für Tisch-Namen ("I2" < "I10")
     - Zeit-Slot-Generierung aus Settings (30-Min-Schritte)
     - Assignment-Picker (single-table + adjacency-combo, gesamtsortiert)
     - Vollständige Rule-Engine (`evaluateReservation`) inkl. Öffnungszeiten,
       Bereichs-Toggle, Spontan-Reserve, Tisch-Verfügbarkeit, Manual-Confirm.

   Imports:
     - utils/helpers: timeToMinutes, minutesToTime
     - core/capacity: alles, was Belegung und Kapazität betrifft

   Backward-Compat: alle Funktionen werden zusätzlich ans `window` gehängt
   (gleiche Strategie wie bei helpers/capacity). Identische Duplikate sind
   vorerst noch in `app.js`.

   Slot-Granularität: Default-Konstante SLOT_STEP_MIN (15, S142) + Single-Source
   getSlotStepMin() (liest settings.slotStepMin, Clamp 5–60) leben hier.
============================================================================ */

import { timeToMinutes, minutesToTime } from '../utils/helpers.js';
import {
  getCurrentSlotDuration,
  getOccupiedTableIds,
  getEligibleTables,
  tableExtraChairs,
  freeSeatsAt,
  violatesClosingTime
} from './capacity.js';

// -----------------------------------------------------------------------------
// Konstanten
// -----------------------------------------------------------------------------
/** Default-Granularität der Slots (Min), wenn kein Setting gesetzt ist.
 *  Fallback 15 (S142 Jonas-Entscheid: 15 ist der neue Standard — deckt sich mit
 *  dem Dropdown-Default, kein Anzeige-≠-Verhalten-Mismatch mehr). Der
 *  konfigurierbare Wert wird über getSlotStepMin() aus settings.slotStepMin
 *  gelesen. */
export const SLOT_STEP_MIN = 15;

/** Clamp-Grenzen für die konfigurierbare Slot-Granularität (Min). */
export const SLOT_STEP_MIN_MIN = 5;
export const SLOT_STEP_MIN_MAX = 60;

// -----------------------------------------------------------------------------
// State-Helper (lazy)
// -----------------------------------------------------------------------------
function getState() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
         || { reservations: [], tables: [], settings: {} };
}

/**
 * Konfigurierbare Zeit-Granularität der Slots (Min) — Single-Source für
 * Buchungsraster (Customer-Form, capacity.js) UND operative Timeline
 * (occupancy.js). Liest settings.slotStepMin, Fallback SLOT_STEP_MIN (15),
 * geclampt auf [5, 60]. NICHT verwechseln mit slotDurationMin (Reservierungs-
 * DAUER, treibt die Sitting-/Kapazitätszahlen — bleibt unangetastet).
 */
export function getSlotStepMin() {
  const state = getState();
  const raw = state && state.settings && state.settings.slotStepMin;
  let n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) n = SLOT_STEP_MIN;
  return Math.max(SLOT_STEP_MIN_MIN, Math.min(SLOT_STEP_MIN_MAX, n));
}

// -----------------------------------------------------------------------------
// Slot-Generierung
// -----------------------------------------------------------------------------

/**
 * Liefert alle 30-Min-Slots im Öffnungszeitfenster. Der LETZTE Slot ist so
 * gewählt, dass eine Reservierung mit Standard-Dauer (Settings.slotDurationMin)
 * exakt zur Schließzeit endet — nicht später. Damit kann das Customer-Form
 * gar keine "über-Feierabend"-Slots mehr anbieten.
 *
 * Beispiel: Öffnung 11:00 / Schließung 23:00 / Dauer 120 Min
 *   → letzter Slot = 23:00 - 120 = 21:00 (endet pünktlich um 23:00).
 *
 * TZ-Vertrag (Audit 2026-05-20, H-07 — siehe docs/timezone-strategy.md):
 *   openingTimeStart/End und die zurückgegebenen Slot-Strings sind
 *   WAND-UHR-ZEIT des Restaurants. Bei der Anzeige im Customer-Form
 *   werden sie 1:1 als Auswahloptionen verwendet. Beim Persistieren in
 *   reservations.time landet ebenfalls die Wand-Uhr-Zeit, nicht in UTC.
 *   DST-Doppel-Stunden-Block (Ende Oktober) ist noch nicht implementiert
 *   — siehe TODO in timezone-strategy.md §3.1.
 */
export function generateTimeSlots() {
  const state = getState();
  if (!state || !state.settings) return [];
  const start = state.settings.openingTimeStart || '11:00';
  const end   = state.settings.openingTimeEnd   || '23:00';
  const duration = getCurrentSlotDuration();
  const slots = [];
  const step = getSlotStepMin();
  const startMin    = timeToMinutes(start);
  const lastStartMin = timeToMinutes(end) - duration;
  if (lastStartMin < startMin) return []; // Dauer länger als Öffnungsfenster
  for (let m = startMin; m <= lastStartMin; m += step) {
    slots.push(minutesToTime(m));
  }
  return slots;
}

// -----------------------------------------------------------------------------
// Natural-Sort
// -----------------------------------------------------------------------------

/** "I2" < "I10" < "I11" — wichtig für Tisch-Listen und natürliche UI-Reihenfolge. */
export function naturalCompare(a, b) {
  const re = /(\d+)|(\D+)/g;
  const partsA = String(a).match(re) || [];
  const partsB = String(b).match(re) || [];
  const len = Math.min(partsA.length, partsB.length);
  for (let i = 0; i < len; i++) {
    const pa = partsA[i], pb = partsB[i];
    const na = parseInt(pa, 10), nb = parseInt(pb, 10);
    if (!isNaN(na) && !isNaN(nb)) {
      if (na !== nb) return na - nb;
    } else {
      if (pa !== pb) return pa.localeCompare(pb);
    }
  }
  return partsA.length - partsB.length;
}

// -----------------------------------------------------------------------------
// Tisch-Geometrie & Adjazenz
// -----------------------------------------------------------------------------

/**
 * Tisch-Bounding-Box in Pixel — muss mit den CSS-Klassen
 * `.table-node.seats-N` synchron bleiben.
 */
export function tableSize(t) {
  const s = Math.min(12, (t && t.seats) || 2);
  const sizes = {
    2:  { w: 70,  h: 60  },
    4:  { w: 90,  h: 80  },
    6:  { w: 110, h: 90  },
    10: { w: 140, h: 110 },
    12: { w: 160, h: 120 }
  };
  if (sizes[s]) return sizes[s];
  if (s <= 2) return sizes[2];
  if (s <= 4) return sizes[4];
  if (s <= 6) return sizes[6];
  if (s <= 10) return sizes[10];
  return sizes[12];
}

/** Abstand zwischen zwei Tisch-Bounding-Boxes (0, wenn sie sich überlappen). */
export function edgeDistance(a, b) {
  const sa = tableSize(a), sb = tableSize(b);
  const ax1 = a.x, ay1 = a.y, ax2 = a.x + sa.w, ay2 = a.y + sa.h;
  const bx1 = b.x, by1 = b.y, bx2 = b.x + sb.w, by2 = b.y + sb.h;
  const xGap = (ax1 > bx2) ? (ax1 - bx2) : (bx1 > ax2) ? (bx1 - ax2) : 0;
  const yGap = (ay1 > by2) ? (ay1 - by2) : (by1 > ay2) ? (by1 - ay2) : 0;
  return Math.hypot(xGap, yGap);
}

/**
 * Schneidet das Liniensegment (x1,y1)-(x2,y2) das axis-aligned Rechteck
 * [rx1,ry1,rx2,ry2]? Verwendet für den Pfad-Blockade-Check in der
 * Adjazenz-Logik. Reine Geometrie, kein DOM, keine State-Abhängigkeit.
 */
export function segmentIntersectsRect(x1, y1, x2, y2, rx1, ry1, rx2, ry2) {
  // 1) Ein Endpunkt der Linie liegt im Rechteck?
  const inside = (x, y) => x >= rx1 && x <= rx2 && y >= ry1 && y <= ry2;
  if (inside(x1, y1) || inside(x2, y2)) return true;
  // 2) Schneidet die Linie eine der vier Rechteck-Kanten?
  function segSeg(ax, ay, bx, by, cx, cy, dx, dy) {
    const d = (bx - ax) * (dy - cy) - (by - ay) * (dx - cx);
    if (d === 0) return false; // parallel
    const t = ((cx - ax) * (dy - cy) - (cy - ay) * (dx - cx)) / d;
    const u = ((cx - ax) * (by - ay) - (cy - ay) * (bx - ax)) / d;
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  }
  return (
    segSeg(x1, y1, x2, y2, rx1, ry1, rx2, ry1) || // top
    segSeg(x1, y1, x2, y2, rx2, ry1, rx2, ry2) || // right
    segSeg(x1, y1, x2, y2, rx1, ry2, rx2, ry2) || // bottom
    segSeg(x1, y1, x2, y2, rx1, ry1, rx1, ry2)    // left
  );
}

/**
 * Liegt ein dritter Tisch (im selben Bereich) im direkten Pfad zwischen
 * den Mittelpunkten von a und b? Wird verwendet, um zu verhindern, dass
 * Tische als adjazent gelten, die zwar im Distanz-Schwellwert liegen,
 * aber visuell durch andere Tische getrennt sind — wenn z.B. I5 und I9
 * in einer Reihe stehen, aber I6, I7, I8 zwischen ihnen, dürfen I5 und
 * I9 nicht zusammengeschoben werden.
 */
function isPathBlockedByOtherTable(a, b) {
  const sa = tableSize(a), sb = tableSize(b);
  const aCx = a.x + sa.w / 2;
  const aCy = a.y + sa.h / 2;
  const bCx = b.x + sb.w / 2;
  const bCy = b.y + sb.h / 2;
  const state = getState();
  for (const c of state.tables) {
    if (c.id === a.id || c.id === b.id) continue;
    if (c.area !== a.area) continue; // nur Tische desselben Bereichs blockieren
    const sc = tableSize(c);
    // Leichte Inflation der Rechteck-Box, damit Tische, die "haarscharf
    // außerhalb" der Linie liegen, nicht unbeabsichtigt nicht-blockieren.
    const pad = 2;
    if (segmentIntersectsRect(aCx, aCy, bCx, bCy,
                              c.x - pad, c.y - pad,
                              c.x + sc.w + pad, c.y + sc.h + pad)) {
      return true;
    }
  }
  return false;
}

/**
 * Zwei Tische gelten als adjazent, wenn:
 *   1. sie im selben Bereich liegen,
 *   2. beide erweiterbar sind,
 *   3. der Kanten-Abstand <= Schwellwert ist (default 55 px),
 *   4. **kein anderer Tisch im direkten Pfad zwischen ihren Mittelpunkten liegt**.
 *
 * Punkt 4 verhindert, dass z.B. I5 und I9 als adjazent gelten, obwohl
 * I6/I7/I8 zwischen ihnen stehen. Vor diesem Fix waren falsche Combos
 * möglich, die physisch nicht zusammengeschoben werden können.
 */
export function areTablesAdjacent(a, b) {
  if (!a || !b || a.id === b.id) return false;
  if (a.area !== b.area) return false;
  if (a.extendable === false || b.extendable === false) return false;
  const state = getState();
  const threshold = state.settings.adjacencyThresholdPx || 55;
  if (edgeDistance(a, b) > threshold) return false;
  if (isPathBlockedByOtherTable(a, b)) return false;
  return true;
}

/** Liefert alle Nachbar-Tisch-IDs eines Tisches. */
export function getTableNeighbors(table) {
  const state = getState();
  return state.tables.filter(t => areTablesAdjacent(table, t)).map(t => t.id);
}

/**
 * Prüft, ob eine Liste von Tisch-IDs ein zusammenhängendes Adjazenz-Cluster
 * bildet (jeder Tisch erreichbar via Adjazenz-Kante).
 */
export function areTablesConnected(tableIds) {
  if (!tableIds || tableIds.length <= 1) return true;
  const state = getState();
  const tables = tableIds.map(id => state.tables.find(t => t.id === id)).filter(Boolean);
  if (tables.length !== tableIds.length) return false;
  const firstArea = tables[0].area;
  if (tables.some(t => t.area !== firstArea || t.extendable === false)) return false;
  // BFS über die Adjazenz-Kanten
  const visited = new Set([tableIds[0]]);
  const queue = [tables[0]];
  while (queue.length) {
    const cur = queue.shift();
    for (const other of tables) {
      if (visited.has(other.id)) continue;
      if (areTablesAdjacent(cur, other)) {
        visited.add(other.id);
        queue.push(other);
      }
    }
  }
  return visited.size === tableIds.length;
}

// -----------------------------------------------------------------------------
// Assignment-Picker
// -----------------------------------------------------------------------------

/**
 * Generiert ALLE plausiblen Assignment-Varianten für eine Reservierungs-
 * Anfrage. Sortiert nach Wunschreihenfolge (kleinste + adjacency-frei zuerst).
 *
 * Jeder Kandidat: { tables, count, area, excess, needsChair, combined, seats, chairsUsed }
 */
export function generateAssignmentOptions(date, time, guests, area, excludeId, durationMinOverride, wishTableId) {
  const state = getState();
  // S134 P2 — Walk-In-Modus gibt die echte Verweildauer aus dem dineInForm
  // mit; sonst die Reservierungs-Standard-Slot-Dauer.
  const durationMin = Number(durationMinOverride) > 0 ? Number(durationMinOverride) : getCurrentSlotDuration();
  const occupied = getOccupiedTableIds(date, time, durationMin, excludeId);
  const eligibleAll = getEligibleTables(area).filter(t => !occupied.has(t.id));

  // Bereichs-Reihenfolge:
  //   Default: indoor → outdoor → club
  //   Gruppen 10-12: club bevorzugt (sweet spot)
  //   Club bei kleinen Gruppen nur als Fallback, wenn explizit angefragt
  const reserveClubForLarge = guests < 10;
  const preferClub = guests >= 10 && guests <= 12;
  let defaultOrder = ['indoor', 'outdoor', 'club'];
  if (preferClub) defaultOrder = ['club', 'indoor', 'outdoor'];
  let allowedAreas = (area && area !== 'any' ? [area] : defaultOrder).filter(a => {
    if (a === 'outdoor' && !state.settings.outdoorEnabled) return false;
    if (a === 'club'    && !state.settings.clubEnabled) return false;
    if (a === 'club'    && reserveClubForLarge && (!area || area === 'any')) return false;
    return true;
  });
  // Last-Resort: falls indoor/outdoor leer, darf der Club doch verwendet werden.
  const fallbackAllowClub = reserveClubForLarge && (!area || area === 'any') && state.settings.clubEnabled;

  const areaOrderIdx = a => {
    const i = defaultOrder.indexOf(a);
    return i < 0 ? 99 : i;
  };

  const candidates = [];
  const seen = new Set();
  // Liefert true zurück, wenn der Kandidat NEU war (Duplikat → false).
  // Der DFS-Cluster-Builder unten verlässt sich auf diesen Rückgabewert,
  // um seinen Hard-Cap-Counter nicht durch Permutationen aufzublähen.
  function add(c) {
    const key = c.tables.slice().sort().join('|') + ':c' + (c.chairsUsed || 0);
    if (seen.has(key)) return false;
    seen.add(key);
    c.needsChair = (c.chairsUsed || 0) > 0;
    candidates.push(c);
    return true;
  }

  // 1) Einzeltische
  function collectSingles(areasList) {
    for (const t of eligibleAll) {
      if (!areasList.includes(t.area)) continue;
      const extra = tableExtraChairs(t);
      if (t.seats >= guests) {
        add({ tables: [t.id], count: 1, area: t.area, seats: t.seats, excess: t.seats - guests, chairsUsed: 0, combined: false });
      } else if (t.seats + extra >= guests) {
        const chairsUsed = guests - t.seats;
        add({ tables: [t.id], count: 1, area: t.area, seats: t.seats + chairsUsed, excess: 0, chairsUsed, combined: false });
      }
    }
  }
  // 2) Tisch-Kombinationen — DFS-Cluster-Expansion (F-03, Session 8.3)
  //
  // Vor F-03 wurde hier das volle Powerset (2^n) aller Bereichs-Tische
  // aufgezählt und jede Combo einzeln auf Adjazenz-Verbundenheit geprüft.
  // Das ist O(2^n) und wurde bei 20–30+ Tischen pro Bereich spürbar langsam
  // (n=30 → ~1 Mrd Subsets), obwohl 99% sofort durch den Connectivity-
  // Check verworfen werden.
  //
  // Neue Variante: DFS, die nur entlang von Adjazenz-Kanten expandiert.
  // Damit ist die Anzahl besuchter Pfade durch die tatsächliche Topologie
  // des Adjazenz-Graphen begrenzt, nicht durch die Powerset-Größe. Bei
  // einer typischen Tischanordnung wachsen die zusammenhängenden Cluster
  // linear mit der Tischzahl, nicht exponentiell.
  //
  // Zusätzliche Speedups:
  //   - Adjazenz-Matrix einmal pro Bereich vorberechnen (n*n Aufrufe von
  //     areTablesAdjacent statt einmal pro Subset).
  //   - Symmetrie-Bruch über den Pool-Index: ein Cluster wird genau einmal
  //     in der eindeutigen "aufsteigenden" Reihenfolge konstruiert.
  //   - Pruning: Cluster mit `cluster.length >= maxCombo` werden nicht
  //     weiter expandiert.
  //   - Hard-Cap MAX_COMBO_CANDIDATES als Sicherheitsnetz für pathologische
  //     Layouts; wird in der Praxis nie ausgereizt.
  function collectCombos(areasList) {
    const MAX_COMBO_CANDIDATES = 200;
    for (const a of areasList) {
      const pool = eligibleAll.filter(t => t.area === a && t.extendable !== false);
      const n = pool.length;
      if (n < 2) continue;
      const maxAreaSeats = pool.reduce((s, t) => s + t.seats + tableExtraChairs(t), 0);
      if (guests > maxAreaSeats) continue;
      const maxCombo = Math.min(n, 6);

      // Adjazenz-Matrix als Set<index> pro Tisch — O(n²) Setup, dann O(1)
      // Lookups in der DFS-Schleife. Bei n=30 sind das 900 Adjazenz-Tests
      // im Setup; in der DFS gibt es keinen einzigen mehr.
      const adjMatrix = new Array(n);
      for (let i = 0; i < n; i++) {
        const neighbors = new Set();
        for (let j = 0; j < n; j++) {
          if (i === j) continue;
          if (areTablesAdjacent(pool[i], pool[j])) neighbors.add(j);
        }
        adjMatrix[i] = neighbors;
      }

      let candidatesFromCombo = 0;
      const cluster = [];           // Pool-Indizes des aktuellen Pfads
      const inCluster = new Array(n).fill(false);

      // Symmetrie-Bruch: jedes Cluster wird genau einmal mit seinem
      // kleinsten Pool-Index als Anker konstruiert. Andere Cluster-Member
      // werden in beliebiger Reihenfolge angehängt, solange ihr Pool-Index
      // strikt größer als der Anker ist. Permutationen innerhalb des
      // Clusters dedupliziert das `seen`-Set über die sortierte Tisch-ID-
      // Liste. Wichtig: lastIdx-Restriktion (alter Versuch) war zu strikt
      // und schloss geometrisch gültige Cluster aus, deren Adjazenz-Pfad
      // nicht der Index-Ordnung folgt — z.B. {I3, I5, I6} mit
      // Adjazenz-Pfad I3-I6-I5, wo I5 keinen direkten Nachbarn mit
      // niedrigerem Index hat.
      function expand(anchorIdx, sumSeats, extraSum) {
        const len = cluster.length;
        // Cluster ab Länge 2 als Kandidat anbieten, wenn ausreichend Plätze.
        // `add` liefert true zurück, falls der Cluster neu war — nur dann
        // zählen wir gegen den MAX-Cap. Sonst würde jede Permutation
        // desselben Clusters den Counter aufblähen.
        if (len >= 2) {
          if (sumSeats >= guests) {
            const added = add({
              tables: cluster.map(idx => pool[idx].id),
              count: len, area: a, seats: sumSeats,
              excess: sumSeats - guests, chairsUsed: 0, combined: true
            });
            if (added) candidatesFromCombo++;
          } else if (sumSeats + extraSum >= guests) {
            const chairsUsed = guests - sumSeats;
            const added = add({
              tables: cluster.map(idx => pool[idx].id),
              count: len, area: a, seats: sumSeats + chairsUsed,
              excess: 0, chairsUsed, combined: true
            });
            if (added) candidatesFromCombo++;
          }
        }

        if (len >= maxCombo) return;
        if (candidatesFromCombo >= MAX_COMBO_CANDIDATES) return;

        // Kandidaten-Indizes: alle, die zu mindestens einem Cluster-Member
        // adjazent sind, noch nicht im Cluster und Pool-Index > anchorIdx.
        for (let j = anchorIdx + 1; j < n; j++) {
          if (inCluster[j]) continue;
          // adjazent zu mindestens einem Cluster-Member?
          let touchesCluster = false;
          for (let k = 0; k < len; k++) {
            if (adjMatrix[cluster[k]].has(j)) { touchesCluster = true; break; }
          }
          if (!touchesCluster) continue;

          cluster.push(j);
          inCluster[j] = true;
          expand(anchorIdx, sumSeats + pool[j].seats, extraSum + tableExtraChairs(pool[j]));
          cluster.pop();
          inCluster[j] = false;

          if (candidatesFromCombo >= MAX_COMBO_CANDIDATES) return;
        }
      }

      // Starte DFS von jedem Tisch als Anker (= kleinster Pool-Index
      // im resultierenden Cluster).
      for (let i = 0; i < n; i++) {
        cluster.push(i);
        inCluster[i] = true;
        expand(i, pool[i].seats, tableExtraChairs(pool[i]));
        cluster.pop();
        inCluster[i] = false;
        if (candidatesFromCombo >= MAX_COMBO_CANDIDATES) break;
      }

      // Audit 2026-05-20, Finding M-10 — Telemetrie für Hard-Cap-Erreichen:
      // Wenn wir den Sicherheits-Cap ausgereizt haben, könnte ein gültiger
      // Cluster außerhalb des Suchraums gelegen haben. Operations soll das
      // sehen, damit ein Layout-Refactor (kleinere Bereiche, weniger Cluster-
      // Dichte) angestoßen werden kann. Aktuell nur console.warn — sobald
      // Sentry da ist (Session 24), sollte das als Breadcrumb gesendet werden.
      if (candidatesFromCombo >= MAX_COMBO_CANDIDATES) {
        console.warn(
          '[reservation-engine] MAX_COMBO_CANDIDATES (' + MAX_COMBO_CANDIDATES + ') erreicht ' +
          'für area=' + a + ', pool=' + n + ' Tische, guests=' + guests + '. ' +
          'Layout könnte zu dicht sein; gültige Cluster könnten unentdeckt bleiben.'
        );
      }
    }
  }
  collectSingles(allowedAreas);
  collectCombos(allowedAreas);
  if (!candidates.length && fallbackAllowClub) {
    collectSingles(['club']);
    collectCombos(['club']);
  }

  // Sortierung (S207-HOTFIX — Jonas-Entscheid „Wunsch-Priorität + Einzeltisch-Vorrang"):
  //   0) WUNSCHTISCH zuerst: feasible Kandidaten, die den Gast-Wunschtisch enthalten,
  //      ranken vor allen anderen (unverbindlich, aber priorisiert — die Engine, nicht
  //      nur die Anzeige, honoriert jetzt den Wunsch). Operator wählt weiterhin frei.
  //   1) EINZELTISCH-VORRANG: weniger Tische zuerst — ein passender Einzeltisch (auch
  //      mit 1 Zusatzstuhl) schlägt JEDE Mehrtisch-Kombi. Vorher gewann eine stuhllose
  //      2er-Kombi gegen einen Einzeltisch mit Stuhl (Kombi = mehr Aufwand, unlogisch).
  //      group = (min(count,3)-1)*2 + (Stühle? 1 : 0):
  //        1 Tisch o. Stuhl=0 · 1 Tisch m. Stuhl=1 · 2 o.=2 · 2 m.=3 · 3+ o.=4 · 3+ m.=5
  //   2) Dann: weniger Tische > weniger Stühle > weniger Überschuss > Bereichs-Präferenz
  const wish = wishTableId || null;
  candidates.sort((a, b) => {
    if (wish) {
      const aw = a.tables.includes(wish) ? 0 : 1;
      const bw = b.tables.includes(wish) ? 0 : 1;
      if (aw !== bw) return aw - bw;
    }
    const group = c => (Math.min(c.count, 3) - 1) * 2 + ((c.chairsUsed || 0) > 0 ? 1 : 0);
    const gA = group(a), gB = group(b);
    if (gA !== gB) return gA - gB;
    if (a.count !== b.count) return a.count - b.count;
    if ((a.chairsUsed || 0) !== (b.chairsUsed || 0)) return (a.chairsUsed || 0) - (b.chairsUsed || 0);
    if (a.excess !== b.excess) return a.excess - b.excess;
    return areaOrderIdx(a.area) - areaOrderIdx(b.area);
  });

  return candidates;
}

/**
 * Single-Best-Assignment (Backward-Compat zum alten Auto-Pick).
 * Liefert die Top-Option oder null. Wird für Feasibility-Checks und den
 * initialen Hint im Customer-Form genutzt.
 */
export function findTableAssignment(date, time, guests, area, excludeId, wishTableId) {
  const options = generateAssignmentOptions(date, time, guests, area, excludeId, undefined, wishTableId);
  if (!options.length) return null;
  const best = options[0];
  return {
    tables: best.tables,
    combined: best.combined,
    needsChair: best.needsChair,
    chairsUsed: best.chairsUsed || 0
  };
}

// -----------------------------------------------------------------------------
// Rule-Engine
// -----------------------------------------------------------------------------

/**
 * Wertet eine Reservierungs-Anfrage gegen alle Regeln aus.
 * Returns { ok, requiresManual, reason, assignment }
 */
export function evaluateReservation(req) {
  const state = getState();
  const { date, time, guests, area } = req;
  // Rule 1: Öffnungszeiten (Start INNERHALB)
  const tMin = timeToMinutes(time);
  if (tMin < timeToMinutes(state.settings.openingTimeStart) ||
      tMin >= timeToMinutes(state.settings.openingTimeEnd)) {
    return { ok: false, reason: 'Außerhalb der Öffnungszeiten.' };
  }
  // Rule 1b: Ende der Reservierung darf nicht NACH Schließung liegen.
  // Wenn req.durationMin gesetzt ist, gewinnt es; sonst Default aus Settings.
  const reqDur = Number(req.durationMin) > 0 ? Number(req.durationMin) : getCurrentSlotDuration();
  const overrun = violatesClosingTime(time, reqDur);
  if (overrun) {
    return {
      ok: false,
      reason: `Reservierung würde um ${overrun.endStr} enden — ${overrun.overrunMin} Min nach Ladenschluss um ${overrun.closeStr}.`
    };
  }
  // Rule 2: Bereich aktiviert?
  if (area === 'outdoor' && !state.settings.outdoorEnabled) {
    return { ok: false, reason: 'Der Außenbereich ist aktuell nicht buchbar.' };
  }
  if (area === 'club' && !state.settings.clubEnabled) {
    return { ok: false, reason: 'Der Clubraum ist aktuell nicht buchbar.' };
  }
  // Rule 3: Spontan-Reserve respektieren — S271 §A: dieselbe Quelle wie das
  // Gastformular (freeSeatsAt): Walk-Ins über der Reserve mindern die freien
  // Plätze. free = total − booked − max(reserve, walkIn).
  // S272-B: mit der Dauer der konkreten Anfrage (reqDur), nicht dem Standard.
  const cap = freeSeatsAt(date, time, req.id, reqDur);
  if (guests > cap.free) {
    const why = cap.walkIn > cap.reserve
      ? `${cap.walkIn} Plätze sind gerade durch Gäste vor Ort belegt`
      : `${cap.reserve} Plätze sind als Spontan-Reserve geblockt`;
    return {
      ok: false,
      reason: `Kapazität für diese Uhrzeit erschöpft (${why}).`
    };
  }
  // Rule 4: passende Tisch-Zuweisung finden (S207-HOTFIX — Wunschtisch priorisieren)
  const assignment = findTableAssignment(date, time, guests, area, req.id, req.wishTableId);
  if (!assignment) {
    return { ok: false, reason: 'Keine passenden Tische im gewünschten Bereich verfügbar.' };
  }
  // Rule 5: manuelle Bestätigung ab Schwellwert
  const requiresManual = guests >= state.settings.manualConfirmThreshold;
  return { ok: true, requiresManual, assignment };
}

// -----------------------------------------------------------------------------
// Backward-Compat: ans window für inline-onclick + klassisches app.js
// -----------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  Object.assign(window, {
    getSlotStepMin,
    generateTimeSlots,
    naturalCompare,
    tableSize,
    edgeDistance,
    areTablesAdjacent,
    getTableNeighbors,
    areTablesConnected,
    generateAssignmentOptions,
    findTableAssignment,
    evaluateReservation
  });
}
